import { Controller } from "@hotwired/stimulus"

// Stroke-tokens picker — bridges 4 design-token tiles (none / subtle /
// caption / bold) to the hidden raw width + colour inputs that
// actually persist on save. Tiles are a UI affordance: clicking one
// writes its (width, colour) tuple onto the raw inputs and fires
// `input` events so the per-template live-preview controller updates
// the on-screen preview the same way it would react to a slider drag.
//
// Tile click → write through to raw inputs → fire input events →
// live preview updates + form persists raw values on save.
//
// When the user instead drags the raw slider or picks a custom
// colour, `#diverge` checks whether the resulting (width, colour)
// still matches any tile. If not, every radio is unchecked so the
// picker honestly shows "no preset matches" rather than lying.
//
// Mounted via `data-controller="stroke-tokens"` on the wrapping
// `.txtreat__stroke` element. Targets:
//   - tile        — each .txtreat__tile <label> (for un-checking on diverge)
//   - widthInput  — the hidden raw slider
//   - colorInput  — the hidden raw colour picker
//   - readout     — the px readout span (kept in sync on tile pick)
//   - advanced    — the <details> Advanced fold (auto-open on diverge)
//
// Token keys + values are read from data-stroke-tokens-{width,color}-param
// on each radio. Keeping the source-of-truth on the server side means
// the JS controller doesn't duplicate the STROKE_TOKENS table.
export default class extends Controller {
  static targets = ["tile", "widthInput", "colorInput", "readout", "advanced"]

  // Tile click — radio's `change` fires this with width + colour
  // coming from data-*-param attributes. Writes through to the raw
  // inputs and fires `input` events so live-preview controllers
  // listening for text_stroke_width / text_stroke_color react.
  pick(event) {
    const radio = event.target
    if (!radio || !radio.checked) return

    const widthRaw = radio.dataset.strokeTokensWidthParam
    const colorRaw = radio.dataset.strokeTokensColorParam
    const width = parseFloat(widthRaw)
    if (!Number.isFinite(width)) return

    if (this.hasWidthInputTarget) {
      this.widthInputTarget.value = String(width)
      this.dispatchInput(this.widthInputTarget)
    }

    // For the "none" tile we leave colour alone — width 0 renders no
    // stroke regardless, and preserving the user's colour means
    // toggling none → subtle restores their previous colour pick.
    if (colorRaw && this.hasColorInputTarget) {
      this.colorInputTarget.value = colorRaw
      this.dispatchInput(this.colorInputTarget)
    }

    if (this.hasReadoutTarget) {
      this.readoutTarget.textContent = `${width}px`
    }
  }

  // Raw input change — if the new (width, colour) tuple no longer
  // matches the currently-checked tile, uncheck every tile so the
  // picker honestly shows "no preset matches" and auto-open the
  // Advanced fold so the creator sees their custom value.
  diverge() {
    const width = this.hasWidthInputTarget
      ? parseFloat(this.widthInputTarget.value)
      : NaN
    const color = this.hasColorInputTarget
      ? String(this.colorInputTarget.value || "").toUpperCase()
      : ""

    let matched = false
    for (const tile of this.tileTargets) {
      const radio = tile.querySelector("input[type='radio']")
      if (!radio) continue
      const tWidth = parseFloat(radio.dataset.strokeTokensWidthParam)
      const tColor = String(radio.dataset.strokeTokensColorParam || "").toUpperCase()

      // "none" tile (no colour) matches on width alone.
      const widthMatch = Number.isFinite(tWidth) && Math.abs(tWidth - width) < 0.001
      const colorMatch = !tColor || tColor === color
      if (widthMatch && colorMatch) {
        radio.checked = true
        matched = true
      } else if (widthMatch && !colorMatch) {
        radio.checked = false
      } else {
        radio.checked = false
      }
    }

    if (!matched && this.hasAdvancedTarget && !this.advancedTarget.open) {
      this.advancedTarget.open = true
    }
  }

  dispatchInput(el) {
    el.dispatchEvent(new Event("input", { bubbles: true }))
    el.dispatchEvent(new Event("change", { bubbles: true }))
  }
}
